import { create } from 'zustand';
import { API_BASE, getToken } from '../lib/api';
import { useSession } from '../lib/session';
import { report } from '../lib/telemetry';
import type { ChatMsg, PeerInfo, PlaybackState, RoomInfo, Stroke } from '../lib/types';
import { deviceId, deviceName } from '../lib/device';
import { clearChatPopups, notifyChat } from '../features/room/chatNotify';
import { preciseNow, type ClientMsg, type ConnStatus, type ServerMsg, type TransportKind, type WorkerIn, type WorkerOut } from './protocol';

export interface FeedItem {
  kind: 'chat' | 'event';
  id: string;
  at: number;
  chat?: ChatMsg;
  text?: string;
}

/** Someone typing in the chat; dropped after a few seconds without news (their tab may have closed). */
export interface Typist {
  peer: string;
  name: string;
  at: number;
}

export interface Reaction {
  id: number;
  emoji: string;
  name: string;
}

interface RoomSync {
  movedTo: string | null; // playback continued on this device's sibling ("iPhone · Safari")
  roomId: string | null;
  status: ConnStatus;
  transport: TransportKind | null;
  detail: string;
  offset: number;
  rtt: number;
  minRtt: number;
  clockSamples: number;
  peerId: string | null;
  room: RoomInfo | null;
  state: PlaybackState | null;
  peers: PeerInfo[];
  feed: FeedItem[];
  reactions: Reaction[];
  typing: Typist[];
  strokes: Stroke[];
  lastError: string | null;

  connect: (roomId: string) => void;
  disconnect: () => void;
  send: (msg: ClientMsg, datagram?: boolean) => void;
}

let worker: Worker | null = null;
const WT_FAIL_KEY = 'vb.wtFailedUA';
let reactionId = 0;

/** Server time "now" in ms, using the worker's offset estimate. */
export const serverNow = () => preciseNow() + useRoomSync.getState().offset;

/** Where the room says playback should be right now. */
export function expectedPosition(s: PlaybackState, now = serverNow()): number {
  if (s.status !== 'playing' || now <= s.refTime) return s.position;
  const p = s.position + ((now - s.refTime) / 1000) * s.rate;
  return s.duration > 0 ? Math.min(p, s.duration) : p;
}

export const useRoomSync = create<RoomSync>((set, get) => ({
  roomId: null,
  status: 'idle',
  transport: null,
  detail: '',
  offset: 0,
  rtt: 0,
  minRtt: 0,
  clockSamples: 0,
  peerId: null,
  room: null,
  state: null,
  peers: [],
  feed: [],
  reactions: [],
  typing: [],
  strokes: [],
  lastError: null,
  movedTo: null,

  connect: (roomId) => {
    get().disconnect();
    const { config, transportPref } = useSession.getState();
    const token = getToken();
    if (!config || !token) return;
    worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<WorkerOut>) => onWorker(e.data);
    const base = API_BASE ? new URL(API_BASE) : location;
    const proto = base.protocol === 'https:' ? 'wss:' : 'ws:';
    // A browser whose WebTransport opened but never answered (Safari on iOS 26.4+ with our server)
    // goes straight to WebSocket next time instead of waiting 4-6 s again. Cleared by a browser update.
    let prefer = transportPref;
    try {
      if (prefer === 'auto' && localStorage.getItem(WT_FAIL_KEY) === navigator.userAgent) prefer = 'websocket';
    } catch {
      /* private mode */
    }
    const msg: WorkerIn = {
      type: 'connect',
      wtUrl: config.wtUrl,
      certHash: config.wtCertHash,
      wsUrl: `${proto}//${base.host}/api/sync/ws`,
      token,
      room: roomId,
      prefer,
      deviceId: deviceId(),
      device: deviceName(),
    };
    clearChatPopups();
    set({ roomId, status: 'connecting', feed: [], peers: [], typing: [], strokes: [], state: null, room: null, lastError: null, detail: '', movedTo: null });
    worker.postMessage(msg);
  },

  disconnect: () => {
    if (worker) {
      worker.postMessage({ type: 'close' } satisfies WorkerIn);
      const w = worker;
      setTimeout(() => w.terminate(), 300);
      worker = null;
    }
    set({ roomId: null, status: 'idle', transport: null, peerId: null, offset: 0, rtt: 0, clockSamples: 0 });
  },

  send: (msg, datagram) => worker?.postMessage({ type: 'send', msg, datagram } satisfies WorkerIn),
}));

function pushFeed(item: FeedItem) {
  const feed = [...useRoomSync.getState().feed, item];
  useRoomSync.setState({ feed: feed.slice(-150) });
}

function onWorker(m: WorkerOut) {
  const set = useRoomSync.setState;
  switch (m.type) {
    case 'status':
      if (m.wtFailed) {
        try {
          localStorage.setItem(WT_FAIL_KEY, navigator.userAgent);
        } catch {
          /* private mode */
        }
      }
      // A working room connection that drops: tell the server why (it only sees the socket end).
      if (m.status === 'reconnecting' && m.detail && useRoomSync.getState().status === 'open')
        report('warn', 'sync', 'room connection dropped', { why: m.detail, transport: useRoomSync.getState().transport, online: navigator.onLine, hidden: document.hidden }, { dedupe: false });
      set({
        status: m.status,
        ...(m.transport ? { transport: m.transport } : {}),
        ...(m.detail !== undefined ? { detail: m.detail } : {}),
        ...(m.status === 'error' ? { lastError: m.detail ?? 'error' } : {}),
      });
      break;
    case 'clock':
      set({ offset: m.offset, rtt: m.rtt, minRtt: m.minRtt, clockSamples: m.samples });
      break;
    case 'msg':
      onServer(m.msg);
      break;
  }
}

function onServer(msg: ServerMsg) {
  const set = useRoomSync.setState;
  switch (msg.t) {
    case 'welcome':
      set({
        peerId: msg.peerId,
        room: msg.room,
        state: msg.state,
        peers: msg.peers,
        feed: (msg.chat ?? []).map((c) => ({ kind: 'chat' as const, id: c.id, at: c.at, chat: c })),
        strokes: msg.strokes ?? [],
      });
      break;
    case 'state':
      set({ state: msg.state });
      break;
    case 'peers': {
      const here = new Set(msg.peers.map((p) => p.id));
      set({ peers: msg.peers, typing: useRoomSync.getState().typing.filter((x) => here.has(x.peer)) });
      break;
    }
    case 'room':
      set({ room: msg.room });
      break;
    case 'chat': {
      const st = useRoomSync.getState();
      pushFeed({ kind: 'chat', id: msg.msg.id, at: msg.msg.at, chat: msg.msg });
      set({ typing: st.typing.filter((x) => x.peer !== msg.msg.from) });
      if (msg.msg.from !== st.peerId) notifyChat(msg.msg);
      break;
    }
    case 'chatreact': {
      const feed = useRoomSync.getState().feed.map((f) => (f.chat?.id === msg.id ? { ...f, chat: { ...f.chat, reactions: msg.reactions } } : f));
      set({ feed });
      break;
    }
    case 'draw': {
      const cur = useRoomSync.getState().strokes;
      if (msg.op === 'stroke') {
        const i = cur.findIndex((x) => x.id === msg.stroke.id);
        if (i < 0) set({ strokes: [...cur, msg.stroke].slice(-300) });
        else {
          const next = cur.slice();
          next[i] = { ...cur[i], pts: [...cur[i].pts, ...msg.stroke.pts], done: msg.stroke.done };
          set({ strokes: next });
        }
      } else if (msg.op === 'erase') set({ strokes: cur.filter((x) => x.id !== msg.id) });
      else set({ strokes: msg.user ? cur.filter((x) => x.user !== msg.user) : [] });
      break;
    }
    case 'typing': {
      const rest = useRoomSync.getState().typing.filter((x) => x.peer !== msg.peer);
      set({ typing: msg.on ? [...rest, { peer: msg.peer, name: msg.name, at: Date.now() }] : rest });
      break;
    }
    case 'react': {
      const r = { id: ++reactionId, emoji: msg.emoji, name: msg.name };
      set({ reactions: [...useRoomSync.getState().reactions.slice(-20), r] });
      break;
    }
    case 'event': {
      if (msg.peer === useRoomSync.getState().peerId) break;
      const text =
        msg.kind === 'joined'
          ? `${msg.name} joined`
          : msg.kind === 'left'
            ? `${msg.name} left`
            : msg.kind === 'host'
              ? `${msg.name} is now the host`
              : `${msg.name} is catching up on their own`;
      pushFeed({ kind: 'event', id: `${msg.kind}-${msg.peer}-${Date.now()}`, at: Date.now(), text });
      break;
    }
    case 'error':
      set({ lastError: msg.message });
      break;
    case 'moved':
      set({ movedTo: msg.device || 'another device' });
      break;
  }
}
